import { accountTypeReducer } from "reducers/accountTypeReducer";

import {
	useClarificationTypes,
	useDocumentCategories,
	useProcurementMethods,
	useUnitsAndMeasures,
} from "./common-types";

export {
	useClarificationTypes,
	useDocumentCategories,
	useProcurementMethods,
	useUnitsAndMeasures,
};

import { combineReducers, configureStore } from "@reduxjs/toolkit";
import { GUESTRFQ_STORAGE } from "constants/guest";
import { SUPPLIERRFQ } from "constants/supplier";
import { accountManagementReducer } from "reducers/accountManagementReducer";
import { companyProfileReducer } from "reducers/companyProfileReducer";
import { globalPresenceReducer } from "reducers/globalPresenceReducer";
import { guestReducer } from "reducers/guestflow/guestReducer";
import { guestRFQReducer } from "reducers/guestflow/guestRFQReducer";
import { rfqReducers } from "reducers/rfqReducer";
import { searchReducer } from "reducers/searchReducer";
import { supplierRFQReducer } from "reducers/supplierflow/supplierRFQReducer";

import authReducer, { logoutUser } from "../features/Auth/authSlice";
import currentCompanyReducer from "../features/Company/currentCompanySlice";
import appMenuReducer from "../features/AppMenu/appMenuSlice";
import { LOCAL_STORAGE_KEYS } from "newui/constants/localStorageKeys";

const appReducer = combineReducers({
	accountTypes: accountTypeReducer,
	search: searchReducer,
	companyProfile: companyProfileReducer,
	globalPresence: globalPresenceReducer,
	auth: authReducer,
	accountManagement: accountManagementReducer,
	rfq: rfqReducers,
	supplier_rfq: supplierRFQReducer,
	guest_rfq: guestRFQReducer,
	guest: guestReducer,

	// New TypeScript slices
	currentCompany: currentCompanyReducer,
	appMenu: appMenuReducer,
});

// Root reducer that handles user logout - resetting all state
const rootReducer = (
	state: ReturnType<typeof appReducer> | undefined,
	action: any
) => {
	if (action.type === logoutUser.type) {
		return appReducer(undefined, action);
	}
	return appReducer(state, action);
};

// Load persisted state from localStorage
function loadPersistedState() {
	try {
		let appMenuState: { isCollapsed: boolean } | undefined;
		const rawAppMenu = localStorage.getItem(LOCAL_STORAGE_KEYS.APP_MENU_STATE);
		if (rawAppMenu) {
			appMenuState = JSON.parse(rawAppMenu);
		}

		return {
			search: localStorage.getItem("search")
				? JSON.parse(localStorage.getItem("search")!)
				: undefined,
			supplier_rfq: localStorage.getItem(SUPPLIERRFQ)
				? JSON.parse(localStorage.getItem(SUPPLIERRFQ)!)
				: undefined,
			guest_rfq: localStorage.getItem(GUESTRFQ_STORAGE)
				? JSON.parse(localStorage.getItem(GUESTRFQ_STORAGE)!)
				: undefined,
			appMenu: appMenuState,
		};
	} catch (error) {
		console.error("Failed to load persisted state:", error);
		return {};
	}
}

// Configure store with Redux Toolkit
export const store = configureStore({
	reducer: rootReducer,
	preloadedState: loadPersistedState(),
	middleware: (getDefaultMiddleware) =>
		getDefaultMiddleware({
			serializableCheck: {
				// Ignore these action types if they contain non-serializable data
				ignoredActions: ["your-action-type-if-needed"],
				// Ignore these paths in the state
				ignoredActionPaths: ["payload.timestamp"],
				ignoredPaths: ["items.dates"],
			},
		}),
	devTools: process.env.NODE_ENV !== "production",
});

// Enable hot module replacement for reducers in development
if (process.env.NODE_ENV !== "production" && (module as any).hot) {
	(module as any).hot.accept(() => {
		store.replaceReducer(rootReducer);
	});
}

// Infer the `RootState` and `AppDispatch` types from the store itself
export type RootState = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch;

export default store;
